"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Listing } from "../lib/types";
import { getMySellerId, getFavorites, getFavoritesAsync, isFavorite, updateFavoriteCache, deleteListingAsync, getListingByIdAsync } from "../lib/storage";
import * as firebaseStorage from "../lib/firebase-storage";
import { auth } from "../lib/firebase";
import { showToast } from "../lib/toast";
import { trackEvent } from "../lib/analytics";

const FALLBACK_USERNAME = "Korisnik";
const sellerUsernameCache = new Map<string, string>();
const sellerUsernameInflight = new Map<string, Promise<string>>();
let favoritesHydrationPromise: Promise<string[]> | null = null;

function getCurrentUserIdentifier(): string {
  if (typeof window === "undefined") return "";
  return (localStorage.getItem("nosiva_user") || "").trim();
}

function getCurrentUserIdentifiers(): string[] {
  const user = auth.currentUser;
  const localFallback = getCurrentUserIdentifier();
  const candidates = [user?.uid || "", user?.email || "", user?.displayName || "", localFallback]
    .map((value) => String(value || "").trim())
    .filter((value) => value.length > 0)
    .map((value) => value.toLowerCase());

  return Array.from(new Set(candidates));
}

function ensureFavoritesHydrated(): Promise<string[]> {
  if (!favoritesHydrationPromise) {
    favoritesHydrationPromise = getFavoritesAsync().finally(() => {
      favoritesHydrationPromise = null;
    });
  }
  return favoritesHydrationPromise;
}

function resolveDisplayUsernameFromListing(sellerId: string, username?: string): string | null {
  const listingUsername = String(username || "").trim();
  if (listingUsername && listingUsername !== FALLBACK_USERNAME) return listingUsername;

  const sellerIdKey = String(sellerId || "").trim();
  if (!sellerIdKey) return FALLBACK_USERNAME;

  return sellerUsernameCache.get(sellerIdKey) || null;
}

async function resolveDisplayUsername(sellerId: string, username?: string): Promise<string> {
  const listingResolved = resolveDisplayUsernameFromListing(sellerId, username);
  if (listingResolved) return listingResolved;

  const sellerIdKey = String(sellerId || "").trim();
  if (!sellerIdKey) return FALLBACK_USERNAME;

  const cached = sellerUsernameCache.get(sellerIdKey);
  if (cached) return cached;

  const inFlight = sellerUsernameInflight.get(sellerIdKey);
  if (inFlight) return inFlight;

  const request = firebaseStorage
    .getProfile(sellerIdKey)
    .then((profile) => {
      const resolved = String(profile?.username || "").trim() || FALLBACK_USERNAME;
      sellerUsernameCache.set(sellerIdKey, resolved);
      return resolved;
    })
    .catch(() => {
      sellerUsernameCache.set(sellerIdKey, FALLBACK_USERNAME);
      return FALLBACK_USERNAME;
    })
    .finally(() => {
      sellerUsernameInflight.delete(sellerIdKey);
    });

  sellerUsernameInflight.set(sellerIdKey, request);
  return request;
}

function ListingCard({ 
  item, 
  showActions = false,
  showAdminDelete = false,
  onDeleted,
  priority = false,
  position,
}: { 
  item: Listing; 
  showActions?: boolean;
  showAdminDelete?: boolean;
  onDeleted?: (id: string) => void;
  /** When true, image loads eagerly (for LCP – e.g. first cards above the fold) */
  priority?: boolean;
  /** 1-based position of the ad within its grid/list, for analytics */
  position?: number;
}) {
  const router = useRouter();
  const [fav, setFav] = useState(false);
  const [loading, setLoading] = useState(false);
  const [displayUsername, setDisplayUsername] = useState<string | null>(null);
  const [disableVideoPreview, setDisableVideoPreview] = useState(true);
  const [imageFailed, setImageFailed] = useState(false);
  const imageSrc =
    (item.imageUrls && item.imageUrls[0]) || item.imageUrl || "/logo-ui.webp.webp";
  const resolvedImageSrc = imageFailed ? "/logo-ui.webp.webp" : imageSrc;
  // Video se učitava tek kad je kartica blizu ekrana (inače bi svi videi u gridu skidali odmah)
  const mediaRef = useRef<HTMLDivElement | null>(null);
  const [videoInView, setVideoInView] = useState(false);
  const shouldRenderVideoPreview = Boolean(item.videoUrl) && !disableVideoPreview && videoInView;

  useEffect(() => {
    if (!item.videoUrl || disableVideoPreview || videoInView) return;
    const el = mediaRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVideoInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVideoInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [item.videoUrl, disableVideoPreview, videoInView]);

  useEffect(() => {
    setImageFailed(false);
  }, [imageSrc]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const connection = (navigator as Navigator & {
      connection?: {
        saveData?: boolean;
        effectiveType?: string;
        addEventListener?: (type: string, handler: () => void) => void;
        removeEventListener?: (type: string, handler: () => void) => void;
      };
    }).connection;

    const update = () => {
      const isMobileViewport = mediaQuery.matches;
      const saveData = Boolean(connection?.saveData);
      const slowConnection =
        typeof connection?.effectiveType === "string" &&
        (connection.effectiveType.includes("2g") ||
          connection.effectiveType.includes("3g"));
      setDisableVideoPreview(isMobileViewport || saveData || slowConnection);
    };

    update();
    mediaQuery.addEventListener("change", update);
    connection?.addEventListener?.("change", update);

    return () => {
      mediaQuery.removeEventListener("change", update);
      connection?.removeEventListener?.("change", update);
    };
  }, []);

  useEffect(() => {
    const listingId = String(item.id);
    let cancelled = false;

    // Prvo instant rezultat iz lokalnog cache-a.
    const cachedFavs = getFavorites();
    setFav(cachedFavs.includes(listingId) || isFavorite(listingId));

    // Ako je korisnik prijavljen, osiguraj jedan zajednički refresh favorites liste
    // umjesto jednog Firestore upita po kartici.
    if (!auth.currentUser) return;

    ensureFavoritesHydrated()
      .then((ids) => {
        if (cancelled) return;
        setFav(ids.includes(listingId));
      })
      .catch((error) => {
        if (process.env.NODE_ENV === "development") {
          console.error("Error hydrating favorites:", error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [item.id]);

  // Load username - first try listing's username, then profile, never show IDs
  useEffect(() => {
    let cancelled = false;
    const sellerId = String(item.sellerId || "");
    const username = item.username;

    const immediate = resolveDisplayUsernameFromListing(sellerId, username);
    if (immediate) {
      setDisplayUsername(immediate);
      return () => {
        cancelled = true;
      };
    }

    async function loadUsername() {
      const resolved = await resolveDisplayUsername(sellerId, username);
      if (cancelled) return;
      setDisplayUsername(resolved);
    }

    loadUsername();

    return () => {
      cancelled = true;
    };
  }, [item.sellerId, item.username]);

  const isMine = useMemo(() => {
    const mySellerId = String(getMySellerId() || "").trim().toLowerCase();
    const myIdentifiers = getCurrentUserIdentifiers();
    const sellerId = String(item.sellerId || "").trim().toLowerCase();
    return sellerId.length > 0 && (sellerId === mySellerId || myIdentifiers.includes(sellerId));
  }, [item.sellerId]);
  const canShowOwnerActions = isMine && showActions;
  const canShowAdminDelete = showAdminDelete && !isMine;

  const handleCardClick = () => {
    // Sačuvaj poziciju skrola i id oglasa da se pri "nazad" vratimo na isto mjesto (i učitamo više ako treba)
    if (typeof window !== "undefined") {
      sessionStorage.setItem("nosiva_marketplace_scroll", String(window.scrollY));
      sessionStorage.setItem("nosiva_marketplace_scroll_listing_id", String(item.id));
    }
    // Prefetch detalja oglasa kako bi stranica detalja bila brža na prvom učitavanju
    getListingByIdAsync(String(item.id)).catch(() => {});

    try {
      const rawPrice: unknown = item.price;
      let numericPrice: number | undefined;

      if (typeof rawPrice === "number") {
        numericPrice = rawPrice;
      } else if (typeof rawPrice === "string") {
        const cleaned = rawPrice.replace(/[^0-9,.-]/g, "").replace(",", ".");
        const parsed = Number(cleaned);
        numericPrice = isNaN(parsed) ? undefined : parsed;
      }

      trackEvent("click_ad", {
        ad_id: String(item.id),
        category: item.category,
        price: numericPrice !== undefined ? numericPrice : undefined,
        position: position ?? undefined,
      });
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("[click_ad] Failed to track click", error);
      }
    }
  };

  const toggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Require authentication for adding/removing favorites
    if (!auth.currentUser) {
      showToast.error("Prijavi se ili registruj da bi mogao sačuvati oglase u favorite.");
      router.push("/login");
      return;
    }

    if (loading) return;
    setLoading(true);

    try {
      const newFavState = await firebaseStorage.toggleFavorite(String(item.id));
      updateFavoriteCache(String(item.id), newFavState);
      setFav(newFavState);
    } catch (error) {
      console.error("Error toggling favorite:", error);
      showToast.error("Greška pri ažuriranju favorita. Pokušaj ponovo.");
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (typeof window === "undefined") return;

    const ok = window.confirm("Jesi sigurna da želiš obrisati ovaj oglas?");
    if (!ok) return;

    if (loading) return;
    setLoading(true);

    try {
      await deleteListingAsync(String(item.id));
      const u = auth.currentUser;
      trackEvent("ad_delete", {
        ad_id: String(item.id),
        full_name: u?.displayName ?? "",
        email: u?.email ?? "",
      });
      showToast.success("Oglas je uspješno obrisan.");
      onDeleted?.(String(item.id));
      router.refresh?.();
      if (canShowAdminDelete && typeof window !== "undefined") {
        window.location.reload();
      }
    } catch (error) {
      console.error("Error deleting listing:", error);
      showToast.error("Greška pri brisanju oglasa. Pokušaj ponovo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Link
      href={`/listing/${item.id}`}
      onClick={handleCardClick}
      className="
        group block rounded-xl sm:rounded-2xl bg-[var(--card)] border border-[var(--border)] overflow-hidden
        transition-all duration-200
        hover:-translate-y-1 hover:shadow-xl hover:border-[var(--accent)]/30
        hover:ring-2 hover:ring-[var(--accent)]/10
      "
    >
      <div className="relative" ref={mediaRef}>
        {shouldRenderVideoPreview ? (
          <video
            src={item.videoUrl}
            className="h-40 sm:h-48 md:h-56 w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            onError={(e) => {
              // Fallback to image if video fails to load
              const target = e.target as HTMLVideoElement;
              target.style.display = 'none';
              const img = target.nextElementSibling as HTMLImageElement;
              if (img) img.style.display = 'block';
            }}
          />
        ) : null}
        <div
          className={`relative h-40 sm:h-48 md:h-56 w-full ${shouldRenderVideoPreview ? "hidden" : ""}`}
        >
          <Image
            src={resolvedImageSrc}
            alt={`${item.title}${item.category ? ` - ${item.category}` : ""}${item.city ? ` u ${item.city}` : ""} - NOSIVA marketplace`}
            fill
            priority={priority}
            loading={priority ? undefined : "lazy"}
            fetchPriority={priority ? "high" : undefined}
            sizes="(max-width: 640px) 50vw, (max-width: 1280px) 33vw, 25vw"
            className="object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            onError={() => {
              if (resolvedImageSrc !== "/logo-ui.webp.webp") {
                setImageFailed(true);
              }
            }}
          />
        </div>

        <div className="absolute top-1.5 sm:top-2 md:top-3 left-1.5 sm:left-2 md:left-3 flex flex-col gap-1 sm:gap-1.5 md:gap-2">
          {item.category ? (
            <span className="text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 md:px-3 py-0.5 sm:py-1 md:py-1.5 rounded-full bg-[var(--card)]/95 border border-[var(--border)] backdrop-blur-sm shadow-sm text-[var(--text)] leading-tight">
              {item.category}
            </span>
          ) : null}
          {item.allowsTrade || item.allowsOffers || typeof item.price === "number" ? (
            <span className="text-[10px] sm:text-xs font-extrabold px-1.5 sm:px-2 md:px-3 py-0.5 sm:py-1 md:py-1.5 rounded-full bg-[var(--accent)] text-white shadow-md leading-tight">
              {item.allowsTrade ? "Zamjena" : item.allowsOffers ? "Po dogovoru" : `${item.price} KM`}
            </span>
          ) : null}
        </div>

        <div className="absolute top-1.5 sm:top-2 md:top-3 right-1.5 sm:right-2 md:right-3 flex flex-col gap-1 sm:gap-1.5 md:gap-2 z-20">
          {canShowOwnerActions ? (
            <>
              <button
                onClick={toggle}
                aria-label="Favorit"
                className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 rounded-full bg-[var(--card)]/95 backdrop-blur-sm border border-[var(--border)] flex items-center justify-center transition-all hover:scale-110 hover:bg-[var(--accent)]/10 hover:border-[var(--accent)]/30 shadow-sm"
                title={fav ? "Ukloni iz favorita" : "Dodaj u favorite"}
              >
                <svg 
                  width="14" 
                  height="14" 
                  viewBox="0 0 24 24" 
                  fill={fav ? "var(--accent)" : "none"} 
                  stroke={fav ? "var(--accent)" : "currentColor"} 
                  strokeWidth="2"
                  className={`${fav ? "text-[var(--accent)]" : "text-[var(--muted)]"} sm:w-4 sm:h-4 md:w-5 md:h-5`}
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  router.push(`/marketplace/${item.id}/edit`);
                }}
                className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 rounded-full bg-[var(--card)]/95 backdrop-blur-sm border-2 border-[var(--accent)]/40 hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)] flex items-center justify-center transition-all shadow-sm"
                title="Uredi oglas"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="sm:w-4 sm:h-4 md:w-[18px] md:h-[18px]">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete(e);
                }}
                className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 rounded-full bg-red-500/95 backdrop-blur-sm border border-red-600/30 hover:bg-red-600 text-white flex items-center justify-center transition-all shadow-sm"
                title="Obriši oglas"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="sm:w-4 sm:h-4 md:w-[18px] md:h-[18px]">
                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            </>
          ) : (
            <>
              {canShowAdminDelete && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDelete(e);
                  }}
                  className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 rounded-full bg-red-500/95 backdrop-blur-sm border border-red-600/30 hover:bg-red-600 text-white flex items-center justify-center transition-all shadow-sm"
                  title="Admin: obriši oglas"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="sm:w-4 sm:h-4 md:w-[18px] md:h-[18px]">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              )}
              <button
                onClick={toggle}
                aria-label="Favorit"
                className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 rounded-full bg-[var(--card)]/95 backdrop-blur-sm border border-[var(--border)] flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 hover:scale-110 hover:bg-[var(--accent)]/10 hover:border-[var(--accent)]/30 shadow-sm"
                title={fav ? "Ukloni iz favorita" : "Dodaj u favorite"}
              >
                <svg 
                  width="14" 
                  height="14" 
                  viewBox="0 0 24 24" 
                  fill={fav ? "var(--accent)" : "none"} 
                  stroke={fav ? "var(--accent)" : "currentColor"} 
                  strokeWidth="2"
                  className={`${fav ? "text-[var(--accent)]" : "text-[var(--muted)]"} sm:w-4 sm:h-4 md:w-5 md:h-5`}
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-2 sm:p-3 md:p-4 lg:p-5 space-y-1.5 sm:space-y-2 md:space-y-3 bg-[var(--card)]">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-extrabold text-xs sm:text-sm md:text-base lg:text-lg leading-tight text-[var(--text)] group-hover:text-[var(--accent)] transition-colors line-clamp-2">
            {item.title}
          </h3>
        </div>

        <div className="text-[10px] sm:text-xs md:text-sm text-[var(--muted)] line-clamp-1">
          {item.brand ? <span className="font-semibold text-[var(--text)]">{item.brand}</span> : null}
          {item.productType ? <span className="text-[var(--muted)]"> • {item.productType}</span> : null}
        </div>

        <div className="flex flex-wrap gap-1 sm:gap-1.5 md:gap-2 text-[10px] sm:text-xs">
          {item.size ? (
            <span className="px-1.5 sm:px-2 md:px-2.5 py-0.5 sm:py-1 rounded-md sm:rounded-lg bg-[var(--bg)] text-[var(--text)] border border-[var(--border)] font-medium">
              {item.size}
            </span>
          ) : null}
          {item.condition ? (
            <span className="px-1.5 sm:px-2 md:px-2.5 py-0.5 sm:py-1 rounded-md sm:rounded-lg bg-[var(--bg)] text-[var(--text)] border border-[var(--border)] font-medium">
              {item.condition}
            </span>
          ) : null}
          {item.city ? (
            <span className="px-1.5 sm:px-2 md:px-2.5 py-0.5 sm:py-1 rounded-md sm:rounded-lg bg-[var(--bg)] text-[var(--text)] border border-[var(--border)] font-medium flex items-center gap-0.5 sm:gap-1">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="sm:w-3 sm:h-3">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span className="hidden sm:inline">{item.city}</span>
            </span>
          ) : null}
          {displayUsername ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                router.push(`/users/${item.sellerId}`);
              }}
              className="px-1.5 sm:px-2 md:px-2.5 py-0.5 sm:py-1 rounded-md sm:rounded-lg bg-[var(--accent)]/10 text-[var(--accent)] border border-[var(--accent)]/20 hover:bg-[var(--accent)] hover:text-white transition cursor-pointer text-left font-medium"
            >
              <span className="hidden sm:inline">@</span>
              {displayUsername}
            </button>
          ) : null}
        </div>
      </div>
    </Link>
  );
}

export default React.memo(ListingCard);
